import { useEffect, useState } from "react";

import { NumericInput } from "@blueprintjs/core";

import { ValidatedNumericInputProps } from "@/components/pages_public/sales-comp/utils/types";

export default function ValidatedNumericInput({
  value,
  onValueChange,
  allowEmpty = true,
  maxDecimals,
  validate,
  ...props
}: ValidatedNumericInputProps) {
  // Track the input string in local state
  const [inputValue, setInputValue] = useState(
    value === 0 ? "" : value.toString(),
  );

  // Update local state when the prop changes (e.g., from external sources)
  useEffect(() => {
    setInputValue(value === 0 && allowEmpty ? "" : value.toString());
  }, [value, allowEmpty]);

  // Check if the input string is valid
  function isValidInput(valueAsString: string): boolean {
    // Empty string check
    if (valueAsString === "") {
      return allowEmpty;
    }

    // Basic number format check
    const basicFormatValid =
      /^-?\d*\.?\d*$/.test(valueAsString) &&
      valueAsString.split(".").length <= 2;

    if (!basicFormatValid) {
      return false;
    }

    // Check if decimal point is allowed
    const isIntegerOnly = maxDecimals === 0;
    if (isIntegerOnly && valueAsString.includes(".")) {
      return false;
    }

    // Check for in-progress decimal
    if (valueAsString.endsWith(".")) {
      // In-progress decimals are allowed only if we allow decimals
      return maxDecimals !== 0;
    }

    // Check for decimal precision if maxDecimals is specified
    if (
      typeof maxDecimals === "number" &&
      maxDecimals > 0 &&
      valueAsString.includes(".")
    ) {
      const parts = valueAsString.split(".");
      if (parts.length > 1 && parts[1].length > maxDecimals) {
        return false;
      }
    }

    // Make sure it parses to a valid number
    const isValidNumber = !isNaN(parseFloat(valueAsString));

    // Run custom validation if provided
    if (validate && isValidNumber) {
      return validate(valueAsString);
    }

    return isValidNumber;
  }

  function handleValueChange(_: number, valueAsString: string) {
    if (isValidInput(valueAsString)) {
      // Update the local input state to maintain the exact string
      setInputValue(valueAsString);

      // Update the parent component state when we have a valid number or empty string
      if (valueAsString === "") {
        onValueChange(0);
      } else if (
        !valueAsString.endsWith(".") &&
        !isNaN(parseFloat(valueAsString))
      ) {
        // Parse the number and optionally apply precision limit
        let parsedValue = parseFloat(valueAsString);

        // Round to specified decimal places if needed
        if (
          typeof maxDecimals === "number" &&
          !isNaN(maxDecimals) &&
          maxDecimals >= 0
        ) {
          const factor = Math.pow(10, maxDecimals);
          parsedValue = Math.round(parsedValue * factor) / factor;
        }

        onValueChange(parsedValue);
      }
      // For in-progress decimals like "1.", we update the local state
      // but don't notify the parent until we have a complete number
    }
    // For invalid inputs, we don't update either state
  }

  return (
    <NumericInput
      value={inputValue}
      onValueChange={handleValueChange}
      {...props}
    />
  );
}
